import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { AlertTriangle, Plus, Trash2, Search, Edit3, Users, Loader2, Check, X } from 'lucide-react';

const EU_ALLERGENS = [
  { key: 'gluten', label: 'Gluten', icon: '🌾' },
  { key: 'crustaceans', label: 'Crustáceos', icon: '🦐' },
  { key: 'eggs', label: 'Huevos', icon: '🥚' },
  { key: 'fish', label: 'Pescado', icon: '🐟' },
  { key: 'peanuts', label: 'Cacahuetes', icon: '🥜' },
  { key: 'soybeans', label: 'Soja', icon: '🫘' },
  { key: 'milk', label: 'Leche / Lácteos', icon: '🥛' },
  { key: 'nuts', label: 'Frutos secos', icon: '🌰' },
  { key: 'celery', label: 'Apio', icon: '🌿' },
  { key: 'mustard', label: 'Mostaza', icon: '🟡' },
  { key: 'sesame', label: 'Sésamo', icon: '🌱' },
  { key: 'sulphites', label: 'Dióxido de azufre', icon: '🍷' },
  { key: 'lupin', label: 'Altramuces', icon: '🌼' },
  { key: 'molluscs', label: 'Moluscos', icon: '🐚' },
];

const DIET_PREFS = ['Vegetariano', 'Vegano', 'Sin gluten', 'Halal', 'Kosher', 'Sin lactosa', 'Diabético'];

function ProfileModal({ open, onClose, initial, onSave }) {
  const empty = { guest_name: '', guest_phone: '', allergens: [], dietary_preferences: [], notes: '' };
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial || empty); }, [open, initial]);

  const toggleAllergen = (key) => setForm(f => ({
    ...f, allergens: f.allergens.includes(key) ? f.allergens.filter(a => a !== key) : [...f.allergens, key]
  }));

  const togglePref = (pref) => setForm(f => ({
    ...f, dietary_preferences: f.dietary_preferences.includes(pref) ? f.dietary_preferences.filter(p => p !== pref) : [...f.dietary_preferences, pref]
  }));

  const save = async () => {
    if (!form.guest_name.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      if (form.id) await api.patch(`/allergens/${form.id}`, form);
      else await api.post('/allergens', form);
      toast.success(form.id ? 'Perfil actualizado' : 'Perfil creado');
      onSave(); onClose();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> {form.id ? 'Editar perfil' : 'Nuevo perfil de alérgenos'}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Nombre del cliente *</Label>
              <Input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Teléfono</Label>
              <Input value={form.guest_phone} onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))} className="text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Alérgenos (14 alérgenos oficiales UE)</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {EU_ALLERGENS.map(a => (
                <button key={a.key} onClick={() => toggleAllergen(a.key)}
                  className={cn("flex items-center gap-2 p-2 rounded-xl border text-left transition-all text-xs",
                    form.allergens.includes(a.key) ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                  <span className="text-base">{a.icon}</span>
                  <span className="flex-1 font-medium">{a.label}</span>
                  {form.allergens.includes(a.key) && <AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Preferencias dietéticas</Label>
            <div className="flex flex-wrap gap-1.5">
              {DIET_PREFS.map(p => (
                <button key={p} onClick={() => togglePref(p)}
                  className={cn("px-3 py-1.5 rounded-full border text-xs font-medium transition-all",
                    form.dietary_preferences.includes(p) ? "border-green-500 bg-green-50 text-green-700" : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Notas adicionales</Label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} placeholder="Ej: Intolerancia severa. Revisar siempre con cocina."
              className="w-full text-xs border border-input rounded-xl px-3 py-2 resize-none outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Guardar perfil
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Allergens() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProfile, setEditProfile] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/allergens'); setProfiles(r.data || []); }
    catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteProfile = async (id) => {
    if (!window.confirm('¿Eliminar este perfil?')) return;
    try { await api.delete(`/allergens/${id}`); fetchAll(); toast.success('Eliminado'); }
    catch { toast.error('Error'); }
  };

  const filtered = profiles.filter(p =>
    p.guest_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.guest_phone?.includes(search)
  );

  const totalAllergens = profiles.reduce((s, p) => s + (p.allergens?.length || 0), 0);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Alertas de Alérgenos" helpSection="allergens" actions={
        <Button onClick={() => { setEditProfile(null); setShowModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo perfil
        </Button>
      } />

      <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Alertas de Alérgenos</h1>
            <p className="text-sm text-slate-500">Registra los alérgenos de tus clientes para alertar al equipo de cocina</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { l: 'Perfiles registrados', v: profiles.length, icon: Users, c: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Alérgenos registrados', v: totalAllergens, icon: AlertTriangle, c: 'text-red-500', bg: 'bg-red-50' },
            { l: 'Alérgenos distintos', v: new Set(profiles.flatMap(p => p.allergens || [])).size, c: 'text-amber-600', bg: 'bg-amber-50', icon: AlertTriangle },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-4 w-4", s.c)} />
                </div>
                <div className="text-2xl font-bold text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente..." className="pl-9 text-sm" />
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Sin perfiles de alérgenos</p>
                <p className="text-sm mt-1 mb-4">Registra los alérgenos de tus clientes para mantener al equipo informado</p>
                <Button onClick={() => { setEditProfile(null); setShowModal(true); }} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Crear primer perfil
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map((p) => (
                  <div key={p.id} className="p-4 hover:bg-slate-50 group">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">{p.guest_name}</p>
                          {p.allergens?.length > 0 && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full flex items-center gap-0.5">
                              <AlertTriangle className="h-2.5 w-2.5" /> {p.allergens.length}
                            </span>
                          )}
                        </div>
                        {p.guest_phone && <p className="text-xs text-slate-400">{p.guest_phone}</p>}
                        {p.allergens?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {p.allergens.map(a => {
                              const meta = EU_ALLERGENS.find(e => e.key === a);
                              return (
                                <span key={a} className="px-2 py-0.5 bg-red-50 border border-red-200 text-red-700 text-[11px] font-medium rounded-full">
                                  {meta?.icon} {meta?.label || a}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {p.dietary_preferences?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {p.dietary_preferences.map(pref => (
                              <span key={pref} className="px-2 py-0.5 bg-green-50 border border-green-200 text-green-700 text-[11px] rounded-full">{pref}</span>
                            ))}
                          </div>
                        )}
                        {p.notes && <p className="text-xs text-slate-500 mt-1.5 italic">"{p.notes}"</p>}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditProfile(p); setShowModal(true); }}
                          className="p-1.5 hover:bg-slate-200 rounded-lg"><Edit3 className="h-3.5 w-3.5 text-slate-500" /></button>
                        <button onClick={() => deleteProfile(p.id)}
                          className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ProfileModal open={showModal} onClose={() => setShowModal(false)} initial={editProfile} onSave={fetchAll} />
    </div>
  );
}
